// whatsapp-scheduler/src/bt-common/notifier/presence.ts
import type { MqttClientLike } from './mqtt-client-like';
import type { WaToolCommand } from '../interfaces/whatsapp-tool-contracts.interface';

export type BrowserType = 'WhatsApp Web' | 'Gateway' | 'Baileys' | 'Website';

export type EngineKind = 'extension' | 'chrome-gateway' | 'baileys';

/** The retained presence record every engine publishes. */
export interface EnginePresence {
    clientId: string;
    app: string;
    ts: number;
    /** Lifecycle + display; the only derived rule is `isAlwaysOn`. */
    engineKind: EngineKind;
    /** Exactly `Object.keys(<ENGINE>_SUPPORTED_COMMANDS)` — a command is supported
     *  exactly when it is listed (no per-command capability flags). */
    commands: readonly WaToolCommand[];
    /** `WIRE_CONTRACT_VERSION` compiled into the engine. */
    contractVersion: number;
    /** Rollout window only (older backends read it). @7.12 delete. */
    browserType: BrowserType;
    /** Display only. @7.12 delete. */
    device?: string;
    /** BE#199 canonical engine type; duplicates `engineKind`. @7.12 delete. */
    engineType?: string;
    /** Extension only: its Team Inbox view is open (the WhatsApp tab's event gate reads it). */
    teamInboxOpen?: boolean;
    /** BE#668: gateway SQLite-writer dropped-op count; present only when > 0. */
    sqliteDropped?: number;
}

/** The ONE engine-selection rule: only a Chrome gateway or a Baileys pod is
 *  always on. The browser extension (runs while its tab is open) and a record
 *  without `engineKind` (an engine older than the contract) are not. */
export function isAlwaysOn(p: Partial<Pick<EnginePresence, 'engineKind'>>): boolean {
    return p.engineKind === 'chrome-gateway' || p.engineKind === 'baileys';
}

export interface BuildPresenceLwtArgs {
    userId: string;
    clientId: string;
}

export function buildPresenceLwt(args: BuildPresenceLwtArgs) {
    return {
        topic: `users/${args.userId}/presence/${args.clientId}`,
        payload: '',
        qos: 1 as const,
        retain: true as const,
        properties: { messageExpiryInterval: 240 },
    };
}

/** What an engine states about itself in its presence record. */
export interface EnginePresenceInput {
    clientId: string;
    browserType: BrowserType;
    engineKind: EngineKind;
    /** `Object.keys(<ENGINE>_SUPPORTED_COMMANDS)`. */
    commands: readonly WaToolCommand[];
    /** `WIRE_CONTRACT_VERSION`. */
    contractVersion: number;
    /**
     * BE#199: the CANONICAL engine type (matches `session.engineType`, e.g. 'baileys-pod').
     * The clientId prefix (`whatsapp_…`) is NOT engine-self-describing — a Baileys pod's id
     * reads identically to a browser extension's — so consumers that need to tell engines
     * apart should key on this, not the clientId. Optional; omitted from the payload when unset.
     */
    engineType?: string;
    device?: string;
    appVersion?: string;
    /** Extension only (its presence extras): the Team Inbox view is open. */
    teamInboxOpen?: boolean;
    /** BE#668: the gateway's cumulative SQLite-writer dropped-op count. Stamped only when > 0,
     *  so a healthy pod (and every browser/extension client) emits an unchanged payload. */
    sqliteDropped?: number;
}

/** The ONE builder of the retained presence record (the pod's PresencePublisher
 *  and the extension's MQTTSubscriber). Optional fields are omitted when unset. */
export function buildEnginePresenceRecord(input: EnginePresenceInput, nowMs: number): EnginePresence {
    return {
        clientId: input.clientId,
        device: input.device ?? 'Desktop',
        browserType: input.browserType,
        engineKind: input.engineKind,
        commands: input.commands,
        contractVersion: input.contractVersion,
        ...(input.engineType ? { engineType: input.engineType } : {}),
        ...(input.teamInboxOpen !== undefined ? { teamInboxOpen: input.teamInboxOpen } : {}),
        ...(input.sqliteDropped && input.sqliteDropped > 0 ? { sqliteDropped: input.sqliteDropped } : {}),
        app: input.appVersion ?? '1.0.0',
        ts: Math.floor(nowMs / 1000),
    };
}

export interface PresencePublisherOptions extends EnginePresenceInput {
    mqtt: MqttClientLike;
    userId: string;
    /**
     * BE#668: optional provider for the gateway's cumulative SQLite-writer dropped-op count.
     * Read FRESH on every heartbeat and passed to the builder as `sqliteDropped` (stamped only
     * when > 0). Lets the backend/forensics see store-write saturation per client without
     * scraping pod logs.
     */
    getSqliteDropped?: () => number;
    heartbeatMs?: number;
    expirySeconds?: number;
    now?: () => number;
    log?: (level: 'warn' | 'debug', msg: string, extra?: unknown) => void;
}

export class PresencePublisher {
    private timer: ReturnType<typeof setInterval> | null = null;
    private stopped = false;
    private readonly topic: string;

    constructor(private readonly opts: PresencePublisherOptions) {
        this.topic = `users/${opts.userId}/presence/${opts.clientId}`;
    }

    start(): void {
        this.stopped = false;
        if (this.timer) { clearInterval(this.timer); this.timer = null; }
        this.publishOnline();
        const interval = this.opts.heartbeatMs ?? 30_000;
        this.timer = setInterval(() => {
            if (!this.stopped) this.publishOnline();
        }, interval);
    }

    stop(): void {
        this.stopped = true;
        if (this.timer) { clearInterval(this.timer); this.timer = null; }
        try {
            this.opts.mqtt.publish(this.topic, '', {
                qos: 1, retain: true,
                properties: { messageExpiryInterval: 1 },
            });
        } catch (err) {
            this.opts.log?.('warn', '[presence] publish offline failed', err);
            /* best-effort; LWT will clear it */
        }
    }

    private publishOnline(): void {
        const now = this.opts.now ?? Date.now;
        // BE#668: read the dropped-op count fresh each beat; the builder stamps it only when > 0.
        const sqliteDropped = this.opts.getSqliteDropped?.();
        const payload = JSON.stringify(buildEnginePresenceRecord({ ...this.opts, sqliteDropped }, now()));
        try {
            this.opts.mqtt.publish(this.topic, payload, {
                qos: 1, retain: true,
                properties: { messageExpiryInterval: this.opts.expirySeconds ?? 240 },
            });
        } catch (err) {
            this.opts.log?.('warn', '[presence] publish online failed', err);
            /* swallow; next heartbeat will retry */
        }
    }
}
